import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import { en } from "./locales/en";
import { es } from "./locales/es";

export const LANGUAGES = ["es", "en"] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "es";

export const resources = { es, en } as const;

/** Espacios de nombres (uno por pantalla o zona). */
export const NAMESPACES = [
  "shared",
  "common",
  "nav",
  "auth",
  "home",
  "certificates",
  "policies",
  "people",
  "activity",
  "organization",
  "my",
  "platform",
  "errors",
] as const satisfies readonly (keyof typeof es)[];

/** Clave de localStorage donde se recuerda el idioma elegido en el menú. */
export const LANGUAGE_STORAGE_KEY = "punto-firma.language";

/**
 * Detección del idioma: `?lng=en` fuerza el idioma (y se recuerda), útil para
 * enlaces y pruebas; si no, el elegido antes en el menú. Sin `navigator`: el
 * del navegador no se mira.
 */
export const DETECTION = {
  order: ["querystring", "localStorage"],
  lookupQuerystring: "lng",
  lookupLocalStorage: LANGUAGE_STORAGE_KEY,
  caches: ["localStorage"],
};

/**
 * i18next con los textos empaquetados (sin backend HTTP: son pocos y así no hay
 * parpadeo). Idioma: **español siempre por defecto**; inglés solo si se elige
 * en el menú de usuario (se recuerda en localStorage) o con `?lng=en`. El
 * idioma del navegador no cuenta.
 *
 * El espacio de nombres por defecto es `shared` porque los componentes copiados
 * de punto-world llaman a `useTranslation()` sin indicarlo; el código propio
 * pide siempre el suyo: `useTranslation("certificates")`.
 */
export const initI18n = ({ lng }: { lng?: Language } = {}) => {
  if (!i18n.isInitialized) {
    void i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        lng,
        supportedLngs: [...LANGUAGES],
        nonExplicitSupportedLngs: true,
        load: "languageOnly",
        fallbackLng: DEFAULT_LANGUAGE,
        defaultNS: "shared",
        ns: [...NAMESPACES],
        interpolation: { escapeValue: false },
        detection: DETECTION,
        react: { useSuspense: false },
        showSupportNotice: false,
      });

    const syncHtmlLang = (language: string) => {
      document.documentElement.lang = language;
    };
    syncHtmlLang(i18n.resolvedLanguage ?? DEFAULT_LANGUAGE);
    i18n.on("languageChanged", syncHtmlLang);
  } else if (lng) {
    void i18n.changeLanguage(lng);
  }
  return i18n;
};

export const currentLanguage = (): Language => {
  const resolved = i18n.resolvedLanguage;
  return (
    LANGUAGES.find((language) => language === resolved) ?? DEFAULT_LANGUAGE
  );
};

export { i18n };
